import React from 'react'
import styled from 'styled-components'
import Plaatjie from '@ubo/plaatjie'
import ParseContent from '../shared/ParseContent'
import ButtonLanding from './ButtonLanding'
import C3Mark from './C3Mark'

const Section = styled.section`
  position: relative;

  @media (min-width: 992px) {
    min-height: 362px;
    /* The gap above is carried by the text block that precedes the band. */
    margin-bottom: 204px;
  }

  @media (max-width: 991.98px) {
    margin-block: 2rem;
  }
`

const Background = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  background-color: ${(props) => props.theme.color.main};

  & > div {
    width: 100%;
    height: 100%;
  }

  & img {
    object-fit: cover;
  }

  /* The band fades into the white page on both sides. */
  &::before,
  &::after {
    position: absolute;
    content: '';
    z-index: 2;
    top: 0;
    height: 100%;
    width: 23%;
  }

  &::before {
    left: 0;
    background: linear-gradient(90deg, #fff 0%, rgba(255, 255, 255, 0) 100%);
  }

  &::after {
    right: 0;
    background: linear-gradient(270deg, #fff 0%, rgba(255, 255, 255, 0) 100%);
  }

  @media (max-width: 575.98px) {
    &::before,
    &::after {
      display: none;
    }
  }
`

const Inner = styled.div`
  position: relative;
  z-index: 3;
  display: flex;
  align-items: center;

  @media (min-width: 992px) {
    min-height: 362px;
  }

  @media (max-width: 991.98px) {
    padding-block: 3rem;
  }
`

const Description = styled.div`
  & p,
  & h2,
  & h3 {
    font-family: ${(props) => props.theme.font.family.secondary};
    color: #fff;
    text-transform: uppercase;
    text-align: right;
    font-weight: 600;
    margin-bottom: 0;
  }

  @media (min-width: 1200px) {
    & p,
    & h2,
    & h3 {
      font-size: 75px;
      line-height: 70px;
    }
  }

  @media (min-width: 992px) and (max-width: 1199.98px) {
    & p,
    & h2,
    & h3 {
      font-size: 54px;
      line-height: 52px;
    }
  }

  @media (max-width: 991.98px) {
    & p,
    & h2,
    & h3 {
      font-size: 40px;
      line-height: 38px;
    }
  }
`

const Subtitle = styled.span`
  display: block;
  color: #d50021;
  font-weight: 700;
  line-height: 25px;
  margin-bottom: 0.5rem;

  @media (min-width: 992px) {
    font-size: 20px;
  }

  @media (max-width: 991.98px) {
    font-size: 16px;
  }
`

const Title = styled.h2`
  color: #fff;
  font-weight: 500;
  margin-bottom: 22px;

  @media (min-width: 992px) {
    font-size: 50px;
    line-height: 60px;
  }

  @media (max-width: 991.98px) {
    font-size: 32px;
    line-height: 34px;
  }
`

// 279 x 60 on the design, with a wider gutter than the default button.
const DownloadButton = styled(ButtonLanding)`
  @media (min-width: 992px) {
    & > a,
    & > button {
      min-height: 60px;
      padding: 18px 21px 18px 26px;
    }
  }
`

const Mark = styled(C3Mark)`
  flex-shrink: 0;
  margin-left: 2rem;

  @media (min-width: 992px) and (max-width: 1199.98px) {
    width: 80px;
    height: 133px;
    margin-left: 1.5rem;
  }

  @media (max-width: 991.98px) {
    width: 60px;
    height: 100px;
    margin-left: 1rem;
  }
`

function HeroDetailLanding({ description, image, subtitle, title, link }) {
  return (
    <Section>
      <Background>
        <Plaatjie image={image} alt="" />
      </Background>

      <Inner className="container">
        <div className="row w-100 align-items-center">
          <div className="col-lg-6 d-flex align-items-center justify-content-center mb-4 mb-lg-0">
            {description && (
              <Description>
                <ParseContent content={description} />
              </Description>
            )}
            <Mark width={114} height={190} />
          </div>

          <div className="col-lg-6 d-flex flex-column justify-content-center">
            {subtitle && <Subtitle>{subtitle}</Subtitle>}
            {title && <Title>{title}</Title>}
            {link && (
              <div>
                <DownloadButton
                  to={link.url}
                  target={link.target}
                  variant="red"
                  notch={16}
                >
                  {link.title}
                </DownloadButton>
              </div>
            )}
          </div>
        </div>
      </Inner>
    </Section>
  )
}

export default HeroDetailLanding
